// No vendor deps, no OTel deps. The attribute model is OTel-shaped (flat, scalar values) so every
// backend can accept it: Datadog takes anything, Faro takes only strings.
export type AttributeValue = string | number | boolean;
export type Attributes = Record<string, AttributeValue>;

export interface ObservabilityProvider {
  event(name: string, attributes: Attributes): void;
  identify(userId: string | undefined, attributes: Attributes): void;
  reset(): void;
}

// Personal data that analytics events carry for Segment and Microsoft Ads but observability must
// not receive. Compared after normalizeKey, so "first_name", "firstName" and "trait_firstName"
// all match. Value redaction does not catch these: an email is only redacted inside a URL path.
// username is deliberately allowed: failed_login_attempt fires before identify, so it is the only way to
// tie a failed login back to an account. It may be an email address.
const PII_KEYS = new Set(["email", "firstname", "lastname"]);

// Segment's stored traits arrive as "trait_<name>" (see analytics/segmentio getUserTraits).
const normalizeKey = (key: string) =>
  key
    .replace(/^trait_/, "")
    .replace(/[^a-z0-9]/gi, "")
    .toLowerCase();

export const isPiiKey = (key: string) => PII_KEYS.has(normalizeKey(key));

/**
 * Converts arbitrary event properties into Attributes:
 * - PII keys (email, first and last name) are dropped
 * - null and undefined values are dropped
 * - Dates become ISO strings (invalid Dates are dropped)
 * - nested objects, arrays, and other non-scalar values are dropped with a warning
 */
export function normalizeAttributes(properties?: Record<string, unknown> | null): Attributes {
  const attributes: Attributes = {};
  if (!properties) {
    return attributes;
  }

  Object.entries(properties).forEach(([key, value]) => {
    if (value === null || value === undefined || isPiiKey(key)) {
      return;
    }
    if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
      attributes[key] = value;
      return;
    }
    if (value instanceof Date) {
      if (!Number.isNaN(value.getTime())) {
        attributes[key] = value.toISOString();
      }
      return;
    }
    console.warn(`Dropping non-scalar observability attribute "${key}"`);
  });

  return attributes;
}
